import { useMemo } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import { useFamilyMatchPlay } from '@tark/shared/hooks';
import { Flag, HeartHandshake, Loader2 } from 'lucide-react';

export function FamilyMatchPlayPanel({ athleteId }: { athleteId: number | null }) {
  const matchPlay = useFamilyMatchPlay(athleteId != null);
  const interest = matchPlay.interests.data?.find((item) => item.athleteId === athleteId);
  const proposals = useMemo(
    () => (matchPlay.proposals.data ?? []).filter((item) => item.ownAthleteId === athleteId),
    [athleteId, matchPlay.proposals.data]
  );
  if (!athleteId) return null;
  const working = matchPlay.saveInterest.isPending || matchPlay.respond.isPending || matchPlay.report.isPending;
  const loadFailed = matchPlay.interests.isError || matchPlay.proposals.isError;

  const save = (changes: Partial<NonNullable<typeof interest>>) => {
    if (!interest) return;
    matchPlay.saveInterest.mutate({
      athleteId,
      optedIn: changes.optedIn ?? interest.optedIn,
      shareFirstName: changes.shareFirstName ?? interest.shareFirstName,
      sharePhoto: changes.sharePhoto ?? interest.sharePhoto,
      shareLevel: changes.shareLevel ?? interest.shareLevel,
      shareAgeBand: changes.shareAgeBand ?? interest.shareAgeBand,
    });
  };

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><HeartHandshake className="h-5 w-5 text-primary" /> Match play</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loadFailed ? (
          <div className="flex min-h-11 items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm text-text-muted">
            <span>Match play could not load.</span>
            <Button size="sm" variant="outline" onClick={() => { void matchPlay.interests.refetch(); void matchPlay.proposals.refetch(); }}>Retry</Button>
          </div>
        ) : null}
        {(matchPlay.interests.isLoading || matchPlay.proposals.isLoading) && !interest ? (
          <p className="flex items-center gap-2 text-sm text-text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading</p>
        ) : interest?.optedIn ? (
          <div className="rounded-lg border border-border p-3">
            <p className="text-sm font-semibold text-text">Open to coach suggestions</p>
            <p className="mt-3 text-xs font-semibold uppercase text-text-muted">Before approval</p>
            <label className="flex min-h-11 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={interest.shareLevel} disabled={working} onChange={() => save({ shareLevel: !interest.shareLevel })} />
              Share playing level
            </label>
            <label className="flex min-h-11 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={interest.shareAgeBand} disabled={working} onChange={() => save({ shareAgeBand: !interest.shareAgeBand })} />
              Share age range
            </label>
            <p className="mt-2 text-xs font-semibold uppercase text-text-muted">After both approve</p>
            <label className="mt-3 flex min-h-11 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={interest.shareFirstName} disabled={working} onChange={() => save({ shareFirstName: !interest.shareFirstName })} />
              Share first name after both families approve
            </label>
            <label className="flex min-h-11 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={interest.sharePhoto} disabled={working} onChange={() => save({ sharePhoto: !interest.sharePhoto })} />
              Share photo after both families approve
            </label>
            <Button className="mt-2" size="sm" variant="ghost" disabled={working} onClick={() => save({ optedIn: false })}>Leave match play</Button>
          </div>
        ) : interest ? (
          <div className="rounded-lg border border-border p-3">
            <p className="text-sm font-semibold text-text">Open to a coach-suggested match?</p>
            <p className="mt-1 text-xs leading-5 text-text-muted">Choose what another family may see before both sides approve.</p>
            <label className="mt-2 flex min-h-11 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={interest.shareLevel} disabled={working} onChange={() => save({ shareLevel: !interest.shareLevel })} />
              Share playing level before approval
            </label>
            <label className="flex min-h-11 items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={interest.shareAgeBand} disabled={working} onChange={() => save({ shareAgeBand: !interest.shareAgeBand })} />
              Share age range before approval
            </label>
            <Button className="mt-3" size="sm" disabled={working} onClick={() => save({ optedIn: true })}>Join match play</Button>
          </div>
        ) : null}

        {proposals.map((proposal) => (
          <div key={proposal.proposalId} className="rounded-lg border border-border p-3">
            {proposal.bothApproved && proposal.candidatePhotoUrl ? (
              <img className="mb-3 h-14 w-14 rounded-full object-cover" src={proposal.candidatePhotoUrl} alt="Suggested player's shared profile" />
            ) : null}
            <p className="text-sm font-semibold text-text">{proposal.bothApproved ? `Match with ${proposal.candidateFirstName || 'another player'}` : 'Coach suggested a compatible match'}</p>
            <p className="mt-1 text-xs text-text-muted">{[proposal.candidateLevelCode, proposal.candidateAgeBand].filter(Boolean).join(' · ') || 'Private match suggestion'}</p>
            {proposal.status === 'SCHEDULED' && proposal.proposedStartsAt ? (
              <p className="mt-2 text-xs font-semibold text-primary">{new Date(proposal.proposedStartsAt).toLocaleString()}{proposal.locationLabel ? ` · ${proposal.locationLabel}` : ''}</p>
            ) : null}
            {proposal.canRespond ? (
              <div className="mt-3 flex gap-2">
                <Button size="sm" disabled={working} onClick={() => matchPlay.respond.mutate({ proposalId: proposal.proposalId, decision: 'APPROVED' })}>Approve</Button>
                <Button size="sm" variant="outline" disabled={working} onClick={() => matchPlay.respond.mutate({ proposalId: proposal.proposalId, decision: 'DECLINED' })}>Decline</Button>
              </div>
            ) : <p className="mt-2 text-xs font-semibold text-primary">{statusLabel(proposal.status, proposal.ownResponseStatus)}</p>}
            <Button
              className="mt-2"
              size="sm"
              variant="ghost"
              disabled={working}
              onClick={() => {
                if (window.confirm('Report this match suggestion for a safety or privacy review?')) {
                  matchPlay.report.mutate({ proposalId: proposal.proposalId, reasonCode: 'SAFETY' });
                }
              }}
            ><Flag className="mr-1 h-4 w-4" /> Report</Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function statusLabel(status: string, own: string) {
  if (status === 'PENDING_APPROVAL' && own === 'APPROVED') return 'You approved · Waiting for the other family';
  if (status === 'READY_FOR_LOGISTICS') return 'Both approved · Coach is setting the time';
  if (status === 'SCHEDULED') return 'Scheduled';
  if (status === 'DECLINED') return 'Suggestion declined';
  if (status === 'CANCELLED') return 'Suggestion closed';
  return 'Waiting for approval';
}
